import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  TextField,
  Chip,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  Alert
} from '@mui/material';
import {
  Settings as SettingsIcon,
  AccessTime as TimeIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarTodayIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useDataProvider } from 'react-admin';

const SettingsContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.grey[900],
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[700]}`,
}));

const SettingsSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[800],
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[600]}`,
}));

const TimePickerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  alignItems: 'center',
  marginTop: theme.spacing(1),
  flexWrap: 'wrap',
}));

const HorizontalRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: theme.spacing(2),
  flexWrap: 'wrap',
}));

// Helper function to format hour number to HH:00 time string
const formatCutoffHour = (hour: number): string => {
  const hourInt = Math.floor(hour); // Handle double values like 2.5 -> 2
  return `${hourInt.toString().padStart(2, '0')}:00`;
};

interface ReportsSettingsProps {
  onSettingsChange?: (settings: {
    businessDayStartTime: string;
    businessDayEndTime: string;
    customTimeEnabled: boolean;
    orderTimestampBasis: 'opened' | 'closed';
  }) => void;
}

export const ReportsSettings: React.FC<ReportsSettingsProps> = ({ onSettingsChange }) => {
  const dataProvider = useDataProvider();
  
  // Load cutoff_hour from tenant attributes, default to 3 if not set
  const [businessDayStartTime, setBusinessDayStartTime] = useState('03:00');
  const [businessDayEndTime, setBusinessDayEndTime] = useState('03:00');
  const [customTimeEnabled] = useState(true);
  
  // Order Timestamp Basis state - Read-only, loaded from tenant attributes
  const [orderTimestampBasis, setOrderTimestampBasis] = useState<'opened' | 'closed'>('opened');

  // Start Day of Business Week state - Read-only, loaded from tenant attributes (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const [startDayOfBusinessWeek, setStartDayOfBusinessWeek] = useState<number>(0);

  // Load cutoff_hour and order_time_basis from tenant attributes via /tenant-dp endpoint
  useEffect(() => {
    const loadTenantAttributes = async () => {
      try {
        const { data: tenants } = await dataProvider.getList('tenant', {
          filter: {},
          pagination: { page: 1, perPage: 1 },
          sort: { field: 'name', order: 'ASC' }
        });
        
        if (tenants && tenants.length > 0) {
          const tenant = tenants[0];
          
          // Load cutoff_hour
          const cutoffHour = tenant?.attributes?.cutoff_hour;
          if (typeof cutoffHour === 'number' && cutoffHour >= 0 && cutoffHour <= 23) {
            const formattedTime = formatCutoffHour(cutoffHour);
            setBusinessDayStartTime(formattedTime);
            setBusinessDayEndTime(formattedTime);
            console.log(`✅ [ReportsSettings] Loaded cutoff_hour from tenant attributes: ${cutoffHour} -> ${formattedTime}`);
          } else {
            console.warn(`⚠️ [ReportsSettings] Invalid cutoff_hour value: ${cutoffHour}, using default 3`);
          }
          
          // Load order_time_basis
          const orderTimeBasis = tenant?.attributes?.order_time_basis;
          if (orderTimeBasis === 'Opened' || orderTimeBasis === 'opened') {
            setOrderTimestampBasis('opened');
            console.log(`✅ [ReportsSettings] Loaded order_time_basis from tenant attributes: ${orderTimeBasis} -> opened`);
          } else if (orderTimeBasis === 'Closed' || orderTimeBasis === 'closed') {
            setOrderTimestampBasis('closed');
            console.log(`✅ [ReportsSettings] Loaded order_time_basis from tenant attributes: ${orderTimeBasis} -> closed`);
          } else {
            // Explicitly default to 'opened' if missing or invalid
            setOrderTimestampBasis('opened');
            console.warn(`⚠️ [ReportsSettings] Invalid or missing order_time_basis value: ${orderTimeBasis}, using default opened`);
          }

          // Load business_week_start_day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
          const businessWeekStartDay = tenant?.attributes?.business_week_start_day;
          if (typeof businessWeekStartDay === 'number' && businessWeekStartDay >= 0 && businessWeekStartDay <= 6) {
            setStartDayOfBusinessWeek(businessWeekStartDay);
            const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            console.log(`✅ [ReportsSettings] Loaded business_week_start_day from tenant attributes: ${businessWeekStartDay} -> ${dayNames[businessWeekStartDay]}`);
          } else {
            // Default to Sunday (0) if missing or invalid
            setStartDayOfBusinessWeek(0);
            console.warn(`⚠️ [ReportsSettings] Invalid or missing business_week_start_day value: ${businessWeekStartDay}, using default Sunday (0)`);
          }
        }
      } catch (error) {
        console.warn('⚠️ [ReportsSettings] Failed to load tenant attributes, using defaults', error);
      }
    };
    
    loadTenantAttributes();
  }, [dataProvider]);

  // Notify parent component of settings when times change
  useEffect(() => {
    onSettingsChange?.({
      businessDayStartTime,
      businessDayEndTime,
      customTimeEnabled,
      orderTimestampBasis,
    });
  }, [businessDayStartTime, businessDayEndTime, customTimeEnabled, orderTimestampBasis, onSettingsChange]);

  // Check if cross-midnight
  const [startHour, startMinute] = businessDayStartTime.split(':').map(Number);
  const [endHour, endMinute] = businessDayEndTime.split(':').map(Number);
  const startTimeMinutes = startHour * 60 + startMinute;
  const endTimeMinutes = endHour * 60 + endMinute;
  const crossesMidnight = endTimeMinutes <= startTimeMinutes;

  return (
    <SettingsContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SettingsIcon sx={{ color: 'grey.400', fontSize: 24 }} />
        <Typography variant="h5" component="h2" sx={{ color: 'white', fontWeight: 600 }}>
          Reports Settings
        </Typography>
        <Chip 
          label="Display Only" 
          size="small" 
          sx={{ 
            backgroundColor: 'grey.700',
            color: 'grey.300',
            fontSize: '0.75rem',
            height: 24
          }} 
        />
      </Box>

      {/* Business Day Configuration */}
      <SettingsSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BusinessIcon sx={{ color: 'grey.400', fontSize: 20 }} />
          <Typography variant="h6" component="h3" sx={{ color: 'white', fontWeight: 600 }}>
            Business Day Configuration
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'grey.400', mb: 2 }}>
          Configure the business day hours for reporting. These settings persist across sessions and affect how reports calculate business days.
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={customTimeEnabled}
              disabled={true}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'primary.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'grey.300' }}>
              Enable Custom Reporting Hours
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {customTimeEnabled && (
          <>
            <TimePickerContainer>
              <TextField
                label="Business Day Start"
                type="time"
                value={businessDayStartTime}
                disabled={true}
                size="small"
                InputLabelProps={{
                  shrink: true,
                  sx: { color: 'grey.400' }
                }}
                InputProps={{
                  sx: { 
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.600',
                    },
                  }
                }}
                sx={{ minWidth: 150 }}
              />
              
              <Typography variant="body2" sx={{ color: 'grey.400' }}>
                to
              </Typography>
              
              <TextField
                label="Business Day End"
                type="time"
                value={businessDayEndTime}
                disabled={true}
                size="small"
                InputLabelProps={{
                  shrink: true,
                  sx: { color: 'grey.400' }
                }}
                InputProps={{
                  sx: { 
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.600',
                    },
                  }
                }}
                sx={{ minWidth: 150 }}
              />

              {crossesMidnight && (
                <Chip 
                  icon={<TimeIcon />}
                  label="Cross-Midnight" 
                  size="small" 
                  color="warning"
                  variant="outlined"
                  sx={{ 
                    color: 'orange',
                    borderColor: 'orange',
                    backgroundColor: 'rgba(255, 165, 0, 0.1)'
                  }}
                />
              )}
            </TimePickerContainer>

            <Typography variant="body2" sx={{ color: 'grey.500', mt: 1, fontStyle: 'italic' }}>
              Business Day: {businessDayStartTime} - {businessDayEndTime} {crossesMidnight ? '(next day)' : ''}
            </Typography>
          </>
        )}
      </SettingsSection>

      <Divider sx={{ my: 3, borderColor: 'grey.700' }} />

      {/* Order Timestamp Basis */}
      <SettingsSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ReceiptIcon sx={{ color: 'grey.400', fontSize: 20 }} />
          <Typography variant="h6" component="h3" sx={{ color: 'white', fontWeight: 600 }}>
            Order Timestamp Basis
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'grey.400', mb: 2 }}>
          Determine whether NEO reporting is based on the timestamp when the order was opened or when the order was closed.
        </Typography>

        <FormControl component="fieldset" disabled={true}>
          <RadioGroup
            value={orderTimestampBasis}
            sx={{ gap: 1 }}
          >
            <FormControlLabel
              value="opened"
              control={
                <Radio
                  sx={{
                    color: 'grey.400',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: 'grey.300' }}>
                  Order Opened Time
                </Typography>
              }
            />
            <FormControlLabel
              value="closed"
              control={
                <Radio
                  sx={{
                    color: 'grey.400',
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: 'grey.300' }}>
                  Order Closed Time
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        <Typography variant="body2" sx={{ color: 'grey.500', mt: 1, fontStyle: 'italic' }}>
          Currently using: <strong>{orderTimestampBasis === 'opened' ? 'Order Opened Time' : 'Order Closed Time'}</strong>
        </Typography>
      </SettingsSection>

      <Divider sx={{ my: 3, borderColor: 'grey.700' }} />

      {/* Start Day of Business Week Setting */}
      <SettingsSection>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarTodayIcon sx={{ color: 'grey.400', fontSize: 20 }} />
          <Typography variant="h6" component="h3" sx={{ color: 'white', fontWeight: 600 }}>
            Start Day of Business Week Setting
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ color: 'grey.400', mb: 2 }}>
          Determines what the first day of the week is for the WTD Date Range Preset when reporting.
        </Typography>

        <FormControl component="fieldset" disabled={true}>
          <Typography variant="body2" sx={{ color: 'grey.300', mb: 1 }}>
            Start day:
          </Typography>
          <HorizontalRadioGroup
            value={startDayOfBusinessWeek.toString()}
            sx={{ mt: 1 }}
          >
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
              <FormControlLabel
                key={day}
                value={index.toString()}
                control={
                  <Radio
                    sx={{
                      color: 'grey.400',
                      '&.Mui-checked': {
                        color: 'primary.main',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'grey.300' }}>
                    {day}
                  </Typography>
                }
              />
            ))}
          </HorizontalRadioGroup>
        </FormControl>
      </SettingsSection>
    </SettingsContainer>
  );
};

